import { Box, Slider, styled, Typography } from '@mui/material';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import { domainEventBus, WindowLevelSetEvent } from '~/domain/events';
import type { Image3D } from '~/domain/patient/image3d';
import {
    formatNormalizedPetIntensity,
    formatPetMaxIntensity,
    hasSuvScale,
} from '~/domain/patient/petImage';
import { WindowLevel } from '~/domain/patient/windowLevel';
import type { PetColorMap } from '~/domain/viewport/fusionBlend';
import { useImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import { useWindowLevelSubscription } from '~/hooks/useWindowLevelSubscription';
import { NO_VALUE } from '~/viewportGrid';
import { NavigateToMaxButton } from './NavigateToMaxButton';
import { OverlayLabel } from '~/components/OverlayLabel';

const RangeSlider = styled(Slider)(({ theme }) => ({
    height: 130,
    width: 12,
    boxSizing: 'border-box',
    pointerEvents: 'auto',
    '& .MuiSlider-thumb': {
        border: '1px solid #fff',
        backgroundColor: `${theme.palette.secondary.main}`,
        width: 16,
        height: 6,
        borderRadius: 0,
    },
    '& .MuiSlider-valueLabel': {
        backgroundColor: 'transparent',
        padding: 0,
        right: '40px',
        left: 'auto',
        top: '10px',
    },
    '& .MuiSlider-mark': {
        background: 'transparent',
    },
    '& .MuiSlider-rail': {
        height: '100%',
        background: 'var(--slider-gradient)',
        opacity: 0.9,
        borderRadius: 0,
    },
    '& .MuiSlider-track': {
        background: 'transparent',
        border: `2px solid ${theme.palette.background.paper}`,
        borderLeft: 'none',
        borderRight: 'none',
        borderRadius: 0,
    },
    '&.Mui-disabled': {
        opacity: theme.palette.action.disabledOpacity,
    },
}));

const ValueLabelBox = styled(Box)(({ theme }) => ({
    ...theme.mixins.glassOverlay,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
}));

const CONVERGENCE_TOLERANCE = 0.015;
const GRADIENT_SAMPLES = 20;
/** Where the reason a locked control gives wraps, so it stays a narrow column. */
const LOCKED_REASON_MAX_WIDTH_PX = 160;

const sampleColorMapAt = (colorMap: PetColorMap, t: number): string => {
    const clampedT = Math.max(0, Math.min(1, t));
    const stops = colorMap.stops;

    let lowerIdx = 0;
    for (let j = 0; j < stops.length - 1; j++) {
        if (
            clampedT >= stops[j].position &&
            clampedT <= stops[j + 1].position
        ) {
            lowerIdx = j;
            break;
        }
    }

    const lower = stops[lowerIdx];
    const upper = stops[Math.min(lowerIdx + 1, stops.length - 1)];

    const range = upper.position - lower.position;
    const localT = range > 0 ? (clampedT - lower.position) / range : 0;

    const r = Math.round(
        (lower.color[0] + (upper.color[0] - lower.color[0]) * localT) * 255
    );
    const g = Math.round(
        (lower.color[1] + (upper.color[1] - lower.color[1]) * localT) * 255
    );
    const b = Math.round(
        (lower.color[2] + (upper.color[2] - lower.color[2]) * localT) * 255
    );

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const buildDynamicGradient = (
    colorMap: PetColorMap,
    lower: number,
    upper: number
): string => {
    const range = upper - lower;
    if (range <= 0.001) {
        return 'linear-gradient(to bottom, transparent, transparent)';
    }
    const toCss = (v: number) => `${((1 - v) * 100).toFixed(2)}%`;
    const stops: string[] = [];
    const topColor = sampleColorMapAt(colorMap, 1.0);
    stops.push(`${topColor} 0%`);
    stops.push(`${topColor} ${toCss(upper)}`);

    for (let i = 1; i <= GRADIENT_SAMPLES; i++) {
        const fraction = i / GRADIENT_SAMPLES;
        const colormapT = 1 - fraction;
        const sliderPos = upper - fraction * range;
        const color = sampleColorMapAt(colorMap, colormapT);
        stops.push(`${color} ${toCss(sliderPos)}`);
    }

    stops.push(`transparent ${toCss(lower - 0.001)}`);
    stops.push(`transparent 100%`);

    return `linear-gradient(to bottom, ${stops.join(', ')})`;
};

interface PetColorwashControlProps {
    /** The PET series the control windows. */
    readonly petImage: Image3D;
    /** The ramp the series is drawn through, which the rail pictures. */
    readonly colorMap: PetColorMap;
    /**
     * Centres the views that draw the series on its maximum, for the "go to
     * the maximum" button. Without it the maximum is named but not
     * navigable.
     */
    readonly navigateToMax?: () => void;
    /** A control shown at the head of the column, above the maximum. */
    readonly headerAction?: ReactNode;
}

/**
 * The PET window as a colour gradient: a two-thumb range over the series'
 * value span, with the maximum named in the series' unit above it and the
 * patient weight the SUV scale rests on beneath that. A drag re-windows
 * the series through the same bus event every other window control
 * publishes, so every readout of that series follows. The window is read
 * from the series, never held here, so a remount changes nothing.
 *
 * A series that is still loading, or whose load ended short, has no
 * measured maximum: the control then names none and is locked, and it
 * unlocks by itself when the last slice arrives. The reason it gives is
 * the one of the series' recorded load progress, so the lock carries no
 * reason while that record reads complete ahead of the series.
 */
export default function PetColorwashControl({
    petImage,
    colorMap,
    navigateToMax,
    headerAction,
}: PetColorwashControlProps) {
    const intl = useIntl();
    const loadGate = useImageCompletenessGate(petImage);
    const maxPixelValue = petImage.maxValue;
    // Null while the series has no measured maximum. Nothing on the
    // control is then scaled by the placeholder that stands in for it.
    const maxIntensityLabel = loadGate.isBlocked
        ? null
        : formatPetMaxIntensity(petImage);
    const isLocked = maxIntensityLabel === null;
    const maxCaption = (
        <>
            <FormattedMessage id="max" />: {maxIntensityLabel ?? NO_VALUE}
        </>
    );
    const windowLevel = useWindowLevelSubscription(petImage);
    const [dragValue, setDragValue] = useState<[number, number] | null>(null);
    const dragRef = useRef<[number, number] | null>(null);

    useEffect(() => {
        dragRef.current = null;
        setDragValue(null);
    }, [petImage]);

    const derivedValue = useMemo<[number, number]>(() => {
        if (isLocked || !windowLevel || maxPixelValue <= 0) {
            return [0, 1];
        }
        const minPx = windowLevel.level - windowLevel.window * 0.5;
        const maxPx = windowLevel.level + windowLevel.window * 0.5;
        return [
            Math.max(0, Math.min(1, minPx / maxPixelValue)),
            Math.max(0, Math.min(1, maxPx / maxPixelValue)),
        ];
    }, [isLocked, windowLevel, maxPixelValue]);

    useEffect(() => {
        const drag = dragRef.current;
        if (!drag) {
            return;
        }
        const [dl, du] = drag;
        const [vl, vu] = derivedValue;
        if (
            Math.abs(dl - vl) < CONVERGENCE_TOLERANCE &&
            Math.abs(du - vu) < CONVERGENCE_TOLERANCE
        ) {
            dragRef.current = null;
            setDragValue(null);
        }
    }, [derivedValue]);

    const patientWeightKg = petImage.patientWeightKg;
    const sliderValue = dragValue ?? derivedValue;

    const gradientColors = useMemo(
        () => buildDynamicGradient(colorMap, sliderValue[0], sliderValue[1]),
        [colorMap, sliderValue]
    );

    const onColorwashValueChange = (
        _e: Event,
        value: number[] | number
    ): void => {
        if (isLocked) {
            return;
        }
        const newValue = value as [number, number];
        const [lower, upper] = newValue;
        // A thumb on the other one is no window: nothing is written, and
        // the thumbs keep the last window written.
        if (upper <= lower) {
            return;
        }
        dragRef.current = newValue;
        setDragValue(newValue);

        const minPx = lower * maxPixelValue;
        const maxPx = upper * maxPixelValue;
        const ww = Math.max(1, Math.round(maxPx - minPx));
        const wc = Math.round((minPx + maxPx) * 0.5);

        domainEventBus.publishSync(
            new WindowLevelSetEvent(
                new WindowLevel(ww, wc, 0, Math.ceil(maxPixelValue)),
                petImage.seriesUid,
                'user'
            )
        );
    };

    if (!windowLevel) {
        return null;
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap={0.25}
        >
            {/* The header control and the maximum read as a pair. */}
            <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                gap={0.75}
            >
                {headerAction}
                {navigateToMax && maxIntensityLabel !== null ? (
                    <NavigateToMaxButton
                        // The label speaks in "SUVmax" when SUV is computable
                        // and "uptake" otherwise; "max intensity" is ambiguous
                        // in a clinical context.
                        label={intl.formatMessage(
                            {
                                id: hasSuvScale(petImage)
                                    ? 'pet.navigateToSuvMax'
                                    : 'pet.navigateToMaxUptake',
                            },
                            { value: maxIntensityLabel }
                        )}
                        onClick={navigateToMax}
                        glass
                        tooltipPlacement="left"
                        sx={{ mb: 1.5, mr: -1.5 }}
                        testId="pet-max-intensity-label"
                    >
                        {maxCaption}
                    </NavigateToMaxButton>
                ) : (
                    <OverlayLabel sx={{ mb: 1.5, mr: -1.5 }}>
                        {maxCaption}
                    </OverlayLabel>
                )}
            </Box>
            {isLocked && loadGate.reason ? (
                <OverlayLabel
                    data-testid="pet-colorwash-locked-reason"
                    fontSize={10}
                    sx={{
                        mt: -1.5,
                        mb: 1.5,
                        mr: -1.5,
                        maxWidth: LOCKED_REASON_MAX_WIDTH_PX,
                        textAlign: 'right',
                    }}
                >
                    {loadGate.reason}
                </OverlayLabel>
            ) : null}
            {patientWeightKg ? (
                <OverlayLabel sx={{ mt: -1.5, mb: 1, mr: -1.5 }} fontSize={10}>
                    <FormattedMessage id="weight" />:{' '}
                    {patientWeightKg.toFixed(1)} kg
                </OverlayLabel>
            ) : null}
            <DisabledHint
                title={isLocked ? loadGate.reason : undefined}
                placement="left"
                keepWrapper
                spanStyle={{ display: 'flex', pointerEvents: 'auto' }}
            >
                <RangeSlider
                    orientation="vertical"
                    value={sliderValue}
                    step={0.01}
                    // Page keys and Shift+Arrow are slice keys of the views,
                    // so they move no thumb.
                    shiftStep={0}
                    disableSwap
                    min={0}
                    max={1}
                    size="small"
                    disabled={isLocked}
                    onChange={onColorwashValueChange}
                    valueLabelDisplay={isLocked ? 'off' : 'on'}
                    data-testid="pet-colorwash-slider"
                    style={
                        {
                            '--slider-gradient': gradientColors,
                        } as React.CSSProperties
                    }
                    valueLabelFormat={(value) => (
                        <ValueLabelBox>
                            <OverlayLabel fontSize={11}>
                                {(value * 100).toFixed(0)}%
                            </OverlayLabel>
                            <Typography fontSize={9} lineHeight={1}>
                                {formatNormalizedPetIntensity(petImage, value)}
                            </Typography>
                        </ValueLabelBox>
                    )}
                />
            </DisabledHint>
        </Box>
    );
}
